import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Loader2, Play, CheckCircle, Truck, Package, Clock, Pencil, Save, X, Plus, Trash2, Search, Download, XCircle, DollarSign, Factory } from 'lucide-react';
import { productionAPI, productAPI, stockAPI, branchAPI } from '../../services/api';
import { getAllowedUOMs, convertRate, convertQtyToBase } from '../../utils/uomConversion';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const statusConfig = {
  'Planned': { color: '#f59e0b', bg: 'bg-amber-50', text: 'text-amber-700', icon: Package },
  'In Progress': { color: '#3b82f6', bg: 'bg-blue-50', text: 'text-blue-700', icon: Play },
  'Completed': { color: '#059669', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle },
  'Dispatched': { color: '#7c3aed', bg: 'bg-purple-50', text: 'text-purple-700', icon: Truck },
  'Cancelled': { color: '#ef4444', bg: 'bg-red-50', text: 'text-red-700', icon: XCircle },
};

const stages = ['Planned', 'In Progress', 'Completed', 'Dispatched'];

const ProductionDetailView = ({ prodId, onBack }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editingMaterials, setEditingMaterials] = useState(false);
  const [editMaterials, setEditMaterials] = useState([]);
  const [savingMaterials, setSavingMaterials] = useState(false);
  const [editingQty, setEditingQty] = useState(false);
  const [editQty, setEditQty] = useState('');
  const [savingQty, setSavingQty] = useState(false);
  const [editingCosts, setEditingCosts] = useState(false);
  const [editCostMaterials, setEditCostMaterials] = useState([]);
  const [savingCosts, setSavingCosts] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearchIdx, setProductSearchIdx] = useState(null);
  const [productSearchField, setProductSearchField] = useState(null); // 'item_id' or 'description'
  const [productSearchText, setProductSearchText] = useState('');
  const [showBranchDialog, setShowBranchDialog] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [savingBranch, setSavingBranch] = useState(false);
  const productSearchRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('prismoline_user') || '{}');

  const generateProductionPDF = () => {
    if (!order) return;
    const pdf = new jsPDF(); const pw = 210; const m = 15; let y = 12;
    pdf.setFillColor(36, 84, 156); pdf.rect(0, 0, pw, 30, 'F');
    pdf.setTextColor(255); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
    pdf.text('SHARDA INFRASOLUTIONS PRIVATE LIMITED', m, 14);
    pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
    pdf.text('Brand: PRISMOLINE | Road Safety & Infrastructure Solutions', m, 21);
    pdf.text('www.prismoline.com', m, 27); y = 36;
    pdf.setTextColor(36, 84, 156); pdf.setFontSize(13); pdf.setFont(undefined, 'bold');
    pdf.text('PRODUCTION ORDER', pw / 2, y, { align: 'center' }); y += 8;
    pdf.setDrawColor(36, 84, 156); pdf.line(m, y, pw - m, y); y += 6;
    pdf.setTextColor(0); pdf.setFontSize(8); pdf.setFont(undefined, 'bold');
    pdf.text(`Production No: ${order.production_number || ''}`, m, y);
    pdf.text(`Status: ${order.status}`, pw / 2, y); y += 5;
    pdf.text(`Date: ${order.date || new Date(order.created_at).toLocaleDateString('en-IN')}`, m, y);
    pdf.text(`Branch: ${order.branch_name || ''}`, pw / 2, y); y += 5;
    if (order.oc_number) { pdf.text(`OC: ${order.oc_number}`, m, y); y += 5; }
    // FG Product
    y += 3;
    pdf.setFillColor(245, 247, 250); pdf.rect(m, y, pw - 2 * m, 14, 'F');
    pdf.setTextColor(100); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
    pdf.text('FINISHED GOOD:', m + 3, y + 5);
    pdf.setTextColor(0); pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
    pdf.text(order.product_name || order.product || '', m + 3, y + 11);
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(8);
    pdf.text(`Qty: ${order.quantity} ${order.unit || ''}`, pw / 2, y + 8);
    if (order.product_sku) pdf.text(`SKU: ${order.product_sku}`, pw / 2, y + 13);
    y += 20;
    // Materials table with cost columns
    pdf.setFillColor(36, 84, 156); pdf.rect(m, y, pw - 2 * m, 7, 'F');
    pdf.setTextColor(255); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
    const headers = ['#', 'Item ID/SKU', 'Description', 'Qty', 'Unit', 'Rate', 'Total Cost'];
    const colX = [m + 2, m + 10, m + 40, m + 100, m + 118, m + 136, m + 158];
    headers.forEach((h, i) => { pdf.text(h, colX[i], y + 5); });
    y += 9;
    pdf.setTextColor(0); pdf.setFont(undefined, 'normal');
    let totalRMCost = 0;
    (order.materials || []).forEach((mat, idx) => {
      if (y > 270) { pdf.addPage(); y = 15; }
      const tc = mat.total_cost || 0;
      totalRMCost += tc;
      pdf.text(`${idx + 1}`, colX[0], y);
      pdf.text(mat.item_id || '', colX[1], y);
      const desc = (mat.item_description || '').length > 35 ? mat.item_description.substring(0, 35) + '...' : (mat.item_description || '');
      pdf.text(desc, colX[2], y);
      pdf.text(`${mat.quantity || 0}`, colX[3], y);
      pdf.text(mat.unit || '', colX[4], y);
      pdf.text(mat.rate != null ? `${mat.rate}` : '-', colX[5], y);
      pdf.text(tc ? `Rs.${tc.toLocaleString('en-IN')}` : '-', colX[6], y);
      y += 5;
    });
    // Cost Summary
    y += 4;
    if (y > 255) { pdf.addPage(); y = 15; }
    pdf.setDrawColor(36, 84, 156); pdf.line(m, y, pw - m, y); y += 5;
    pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
    const fgQty = order.quantity || 1;
    const fgUnitCost = fgQty > 0 ? totalRMCost / fgQty : 0;
    const totalFGValue = fgQty * fgUnitCost;
    pdf.text('Cost Summary', m, y); y += 5;
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    pdf.text(`Total Raw Material Cost:`, m + 3, y);
    pdf.text(`Rs. ${totalRMCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, m + 80, y); y += 4;
    pdf.text(`FG Unit Cost (RM Cost / FG Qty):`, m + 3, y);
    pdf.text(`Rs. ${fgUnitCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, m + 80, y); y += 4;
    pdf.text(`Total FG Produced Value (${fgQty} x ${fgUnitCost.toFixed(2)}):`, m + 3, y);
    pdf.text(`Rs. ${totalFGValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, m + 80, y); y += 4;
    const matched = Math.abs(totalRMCost - totalFGValue) < 0.01;
    pdf.setFont(undefined, 'bold');
    pdf.text(`Match Status: ${matched ? 'MATCHED' : 'MISMATCH'}`, m + 3, y); y += 6;
    // Audit trail
    if (order.audit_trail?.length > 0) {
      y += 2; if (y > 260) { pdf.addPage(); y = 15; }
      pdf.setFont(undefined, 'bold'); pdf.setFontSize(9);
      pdf.text('Audit Trail', m, y); y += 5;
      pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
      order.audit_trail.forEach(a => {
        if (y > 275) { pdf.addPage(); y = 15; }
        pdf.text(`${a.action} by ${a.user || ''} — ${new Date(a.timestamp).toLocaleString('en-IN')}`, m + 3, y);
        y += 4;
      });
    }
    // Footer
    y = Math.max(y + 8, 268); pdf.setDrawColor(36, 84, 156); pdf.line(m, y, pw - m, y); y += 4;
    pdf.setFontSize(6); pdf.setTextColor(130);
    pdf.text('This is a system-generated Production Order from Prismoline ERP.', pw / 2, y, { align: 'center' });
    pdf.save(`Production_${order.production_number || order.id}.pdf`);
  };
  const canUpdate = ['Admin', 'Manager', 'Operator'].includes(user.role);

  /**
   * Refresh `current_stock` for each material against the live stock balance.
   *
   * The Production Order document stores a snapshot of `current_stock` at the time
   * the PRD was created/saved. That value goes stale as inwards / STN-In / sales-returns /
   * adjustments happen afterwards. This helper resolves each material's SKU to its product
   * UUID, calls `/api/stock/batch-net-qty` (the unified movement-balance endpoint), and
   * converts the result from the product's base unit into the material's display unit so
   * the comparison `current_stock >= required_qty` is always accurate.
   */
  const refreshMaterialStocks = async (mats, branch) => {
    if (!Array.isArray(mats) || mats.length === 0) return mats;
    // Step 1 — resolve every material's product UUID + base unit + conversions
    const resolved = await Promise.all(mats.map(async (mat) => {
      if (!mat || !mat.item_id) return mat;
      // Skip product lookup if we already have UUID + conversion data from a prior enrichment
      if (mat._productUUID && mat._baseUnit) return mat;
      try {
        const products = await productAPI.getAll({ search: mat.item_id, page_size: 5 });
        const items = Array.isArray(products) ? products : (products?.items || []);
        const prod = items.find(p => p.sku === mat.item_id || p.id === mat.item_id) || items[0];
        if (!prod) return mat;
        return {
          ...mat,
          _productUUID: prod.id,
          _conversions: prod.unit_conversions || prod.conversions || [],
          _baseUnit: prod.unit || 'PCS',
          _allowedUOMs: getAllowedUOMs(prod),
        };
      } catch { return mat; }
    }));
    // Step 2 — batch fetch live stock for all resolved UUIDs
    const productIds = resolved.map(m => m?._productUUID).filter(Boolean);
    if (productIds.length === 0) return resolved;
    let stockMap = {};
    let detailsMap = {};
    try {
      const data = await stockAPI.batchNetQty(branch || '', productIds);
      stockMap = data?.stock || {};      // available (= physical − awaiting-invoice reserved)
      detailsMap = data?.details || {};  // {physical_balance, reserved_awaiting_invoice, available}
    } catch { return resolved; }
    // Step 3 — apply live qty. If the material's display unit matches the product
    // base unit, no conversion needed. If they differ AND a valid conversion exists,
    // convert. If they differ AND NO conversion exists, surface the raw base-unit
    // qty + a clear unit_mismatch flag so the UI shows a ⚠️ warning instead of
    // silently rendering a meaningless number (e.g. "3.455 Roll" mislabelled as Sqft).
    const findConversion = (conversions, fromU, toU) =>
      (conversions || []).find(c =>
        (c.from_unit === fromU && c.to_unit === toU) ||
        (c.from_unit === toU && c.to_unit === fromU)
      );
    return resolved.map(mat => {
      if (!mat || !mat._productUUID || !(mat._productUUID in stockMap)) return mat;
      const baseQty = Number(stockMap[mat._productUUID]) || 0;
      const det = detailsMap[mat._productUUID] || {};
      const basePhysical = Number(det.physical_balance) || 0;
      const baseReserved = Number(det.reserved_awaiting_invoice) || 0;
      const baseUnit = mat._baseUnit;
      const displayUnit = mat.unit;
      const conversions = mat._conversions || [];
      const unitsMatch = !displayUnit || !baseUnit || displayUnit === baseUnit;
      const hasConv = !unitsMatch && !!findConversion(conversions, baseUnit, displayUnit);
      const unitMismatch = !unitsMatch && !hasConv;

      let displayQty = baseQty;
      let displayPhysical = basePhysical;
      let displayReserved = baseReserved;
      let stockUnit = displayUnit;

      if (unitMismatch) {
        // Show in BASE unit so the user sees the truth; UI will render a ⚠️ warning.
        stockUnit = baseUnit;
      } else if (hasConv) {
        displayQty = convertQtyToBase(conversions, baseUnit, baseQty, displayUnit);
        displayPhysical = convertQtyToBase(conversions, baseUnit, basePhysical, displayUnit);
        displayReserved = convertQtyToBase(conversions, baseUnit, baseReserved, displayUnit);
      }
      return {
        ...mat,
        current_stock: Math.round(displayQty * 100) / 100,
        physical_stock: Math.round(displayPhysical * 100) / 100,
        reserved_stock: Math.round(displayReserved * 100) / 100,
        stock_unit: stockUnit,
        unit_mismatch: unitMismatch,
      };
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await productionAPI.getById(prodId);
        if (cancelled) return;
        setOrder(d);
        // Refresh materials' Available Stock against live balance — avoids stale snapshot
        if (d?.materials?.length > 0) {
          const refreshed = await refreshMaterialStocks(d.materials, d.branch);
          if (cancelled) return;
          setOrder(prev => prev ? { ...prev, materials: refreshed } : prev);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [prodId]);

  // Fetch Production branches once
  useEffect(() => {
    branchAPI.getAll().then(data => {
      const items = Array.isArray(data) ? data : (data?.items || []);
      setBranches(items.filter(b => b.type === 'Production'));
    }).catch(() => {});
  }, []);

  // Open dialog — preselect current branch
  useEffect(() => {
    if (showBranchDialog) setSelectedBranchId(order?.branch || '');
  }, [showBranchDialog, order?.branch]);

  const saveBranch = async () => {
    if (!selectedBranchId) return;
    setSavingBranch(true);
    try {
      await productionAPI.update(prodId, { branch: selectedBranchId });
      const fresh = await productionAPI.getById(prodId);
      setOrder(fresh);
      setShowBranchDialog(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update branch');
    } finally {
      setSavingBranch(false);
    }
  };

  // Load all products when edit mode starts
  useEffect(() => {
    if (editingMaterials && allProducts.length === 0) {
      productAPI.getAll({ page_size: 500 }).then(data => {
        const items = Array.isArray(data) ? data : (data?.items || data || []);
        setAllProducts(items);
      }).catch(() => {});
    }
  }, [editingMaterials, allProducts.length]);

  // Close product search dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (productSearchRef.current && !productSearchRef.current.contains(e.target)) {
        setProductSearchIdx(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredProducts = allProducts.filter(p => {
    const q = productSearchText.toLowerCase().trim();
    if (!q) return true; // Show all when empty
    return (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q);
  }).slice(0, 15);

  const selectProduct = async (idx, product) => {
    const conversions = product.unit_conversions || product.conversions || [];
    const allowedUOMs = getAllowedUOMs(product);
    const baseUnit = product.unit || 'PCS';
    const unit = product.unit || product.buy_unit || 'PCS';

    setEditMaterials(prev => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        item_id: product.sku || product.id,
        item_description: product.name,
        unit,
        rate: product.cost_price || null,
        _conversions: conversions,
        _allowedUOMs: allowedUOMs,
        _baseUnit: baseUnit,
        _baseQty: updated[idx].quantity || 0,
        _productUUID: product.id,
      };
      if (updated[idx].quantity && product.cost_price) {
        updated[idx].total_cost = Math.round(updated[idx].quantity * product.cost_price * 100) / 100;
      }
      return updated;
    });
    setProductSearchIdx(null);
    setProductSearchText('');

    // Fetch current stock for the selected product — works even if order has no branch (returns total across branches)
    if (product.id) {
      try {
        const stockData = await stockAPI.batchNetQty(order?.branch || '', [product.id]);
        const netQty = stockData.stock?.[product.id] ?? 0;
        setEditMaterials(prev => {
          const updated = [...prev];
          if (updated[idx]) updated[idx].current_stock = netQty;
          return updated;
        });
      } catch { /* ignore stock fetch errors */ }
    }
  };

  const updateStatus = async (newStatus) => {
    if (!order) return;
    const currentIdx = stages.indexOf(order.status);
    const nextIdx = stages.indexOf(newStatus);
    if (nextIdx !== currentIdx + 1) { alert('Status must progress sequentially'); return; }
    
    // Block In Progress if any material has insufficient stock OR unit mismatch
    if (newStatus === 'In Progress') {
      const unitMismatchMats = (order.materials || []).filter(m => m.unit_mismatch);
      if (unitMismatchMats.length > 0) {
        const details = unitMismatchMats.map(m =>
          `${m.item_description || m.item_id}: BOM uses ${m.unit} but product is tracked in ${m.stock_unit}`
        ).join('\n');
        alert(`Cannot start production — unit mismatch on these materials:\n${details}\n\nFix: open the product master and add a ${unitMismatchMats[0].stock_unit}↔${unitMismatchMats[0].unit} conversion factor; OR re-enter the BOM in the product's native unit.`);
        return;
      }
      const insufficientMats = (order.materials || []).filter(m =>
        !m.unit_mismatch && m.current_stock !== undefined && m.current_stock < Number(m.quantity || 0)
      );
      if (insufficientMats.length > 0) {
        const details = insufficientMats.map(m => 
          `${m.item_description || m.item_id}: need ${m.quantity} ${m.unit}, have ${m.current_stock}`
        ).join('\n');
        alert(`Cannot start production — insufficient stock for:\n${details}\n\nPlease arrange stock before starting production.`);
        return;
      }
      if (!window.confirm('Starting production will auto-deduct materials from stock (FIFO). Continue?')) return;
    }

    try {
      setUpdating(true);
      const updated = await productionAPI.update(prodId, { status: newStatus });
      setOrder(updated);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update'); }
    finally { setUpdating(false); }
  };

  const cancelProduction = async () => {
    if (!order) return;
    const msg = order.stock_deducted
      ? 'This will CANCEL the production order and REVERSE all stock transactions (material deductions and FG stock additions). This cannot be undone. Continue?'
      : 'This will cancel the production order. Continue?';
    if (!window.confirm(msg)) return;
    try {
      setUpdating(true);
      const updated = await productionAPI.update(prodId, { status: 'Cancelled' });
      setOrder(updated);
      toast.success('Production order cancelled successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setUpdating(false);
    }
  };

  const startEditCosts = () => {
    const mats = (order.materials || []).map(m => ({ ...m }));
    setEditCostMaterials(mats);
    setEditingCosts(true);
  };

  const updateCostField = (idx, field, value) => {
    setEditCostMaterials(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'rate') {
        const qty = parseFloat(updated[idx].quantity) || 0;
        const rate = parseFloat(value) || 0;
        updated[idx].total_cost = rate > 0 ? Math.round(qty * rate * 100) / 100 : null;
      }
      return updated;
    });
  };

  const saveCosts = async () => {
    try {
      setSavingCosts(true);
      const updated = await productionAPI.updateCosts(prodId, editCostMaterials);
      setOrder(updated);
      setEditingCosts(false);
      toast.success('Cost prices updated successfully. FG stock cost recalculated.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update costs');
    } finally {
      setSavingCosts(false);
    }
  };

  const startEditMaterials = async () => {
    const mats = (order.materials || []).map(m => ({ ...m, current_stock: m.current_stock }));
    setEditMaterials(mats);
    setEditingMaterials(true);

    // Enrich existing materials with UoM conversion data from product master
    const enriched = [...mats];
    let changed = false;
    for (let i = 0; i < enriched.length; i++) {
      const mat = enriched[i];
      if (!mat.item_id) continue;
      try {
        const products = await productAPI.getAll({ search: mat.item_id, page_size: 5 });
        const items = Array.isArray(products) ? products : (products?.items || []);
        const prod = items.find(p => p.sku === mat.item_id || p.id === mat.item_id) || items[0];
        if (prod) {
          const conversions = prod.unit_conversions || prod.conversions || [];
          if (conversions.length > 0) {
            enriched[i] = {
              ...enriched[i],
              _conversions: conversions,
              _allowedUOMs: getAllowedUOMs(prod),
              _baseUnit: prod.unit || 'PCS',
              _baseQty: mat.quantity,
              _productUUID: prod.id,
            };
            changed = true;
          } else if (prod.id) {
            enriched[i]._productUUID = prod.id;
            enriched[i]._baseUnit = prod.unit || 'PCS';
            changed = true;
          }
        }
      } catch { /* skip */ }
    }
    // Refresh live stock for every material — replaces the stale snapshot
    // saved in the PRD document with the current movement-balance value.
    const refreshed = await refreshMaterialStocks(enriched, order?.branch);
    setEditMaterials(refreshed);
    if (changed) { /* enrichment already applied via refreshed */ }
  };

  const cancelEditMaterials = () => {
    setEditingMaterials(false);
    setEditMaterials([]);
  };

  const updateMaterialField = (idx, field, value) => {
    setEditMaterials(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(updated[idx].quantity) || 0;
        const rate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(updated[idx].rate) || 0;
        updated[idx].total_cost = rate > 0 ? Math.round(qty * rate * 100) / 100 : null;
      }
      return updated;
    });
  };

  const changeMaterialUnit = (idx, newUnit) => {
    setEditMaterials(prev => {
      const updated = [...prev];
      const mat = updated[idx];
      const conversions = mat._conversions || [];
      const baseUnit = mat._baseUnit || mat.unit;
      const baseQty = mat._baseQty || mat.quantity;
      if (conversions.length > 0 && baseUnit) {
        const convertedQty = convertQtyToBase(conversions, baseUnit, baseQty, newUnit);
        const convertedRate = mat.rate != null ? convertRate(conversions, baseUnit, mat.rate, newUnit) : mat.rate;
        const qty = Math.round(convertedQty * 100) / 100;
        // Convert current_stock from base unit to new unit so comparison + display are in same UoM
        let convertedStock = mat.current_stock;
        if (mat.current_stock != null && !Number.isNaN(Number(mat.current_stock))) {
          const cs = convertQtyToBase(conversions, baseUnit, Number(mat.current_stock), newUnit);
          convertedStock = Math.round(cs * 100) / 100;
        }
        updated[idx] = {
          ...mat,
          unit: newUnit,
          quantity: qty,
          rate: convertedRate,
          total_cost: convertedRate != null && qty > 0 ? Math.round(qty * convertedRate) : mat.total_cost,
          current_stock: convertedStock,
        };
      } else {
        updated[idx] = { ...mat, unit: newUnit };
      }
      return updated;
    });
  };

  const addMaterialRow = () => {
    const maxSeq = editMaterials.reduce((max, m) => {
      const n = parseInt(m.seq) || 0;
      return n > max ? n : max;
    }, 0);
    setEditMaterials(prev => [...prev, {
      seq: String(maxSeq + 1), item_id: '', item_description: '', quantity: 0, unit: 'PCS', rate: null, total_cost: null, is_alternate: false,
    }]);
  };

  const removeMaterialRow = (idx) => {
    setEditMaterials(prev => prev.filter((_, i) => i !== idx));
  };

  const saveMaterials = async () => {
    try {
      setSavingMaterials(true);
      // Strip transient frontend-only fields before sending to backend
      const cleanMaterials = editMaterials.map(({ _conversions, _allowedUOMs, _baseUnit, _baseQty, _productUUID, ...rest }) => rest);
      const updated = await productionAPI.updateMaterials(prodId, cleanMaterials);
      setOrder(updated);
      setEditingMaterials(false);
      toast.success('Materials updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update materials');
    } finally {
      setSavingMaterials(false);
    }
  };

  const saveQuantity = async () => {
    const newQty = parseFloat(editQty);
    if (!newQty || newQty <= 0) { toast.error('Quantity must be greater than 0'); return; }
    try {
      setSavingQty(true);
      const updated = await productionAPI.update(prodId, { quantity: newQty });
      setOrder(updated);
      setEditingQty(false);
      toast.success(`FG quantity updated to ${newQty}. Materials recalculated.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update quantity');
    } finally {
      setSavingQty(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!order) return <div className="p-5"><button onClick={onBack} className="text-xs text-gray-500"><ArrowLeft className="inline w-3 h-3" /> Back</button><p className="mt-4">Not found</p></div>;

  const sc = statusConfig[order.status] || statusConfig['Planned'];
  const StatusIcon = sc.icon;
  const currentStageIdx = stages.indexOf(order.status);
  const nextStatus = currentStageIdx < stages.length - 1 ? stages[currentStageIdx + 1] : null;
  const isPlanned = order.status === 'Planned';
  const isCancelled = order.status === 'Cancelled';
  const isCompletedOrDispatched = ['Completed', 'Dispatched'].includes(order.status);

  // Cost calculations
  const totalRMCost = (order.materials || []).reduce((sum, m) => sum + (m.total_cost || 0), 0);
  const fgQty = order.quantity || 1;
  const fgUnitCost = fgQty > 0 ? totalRMCost / fgQty : 0;
  const totalFGValue = fgQty * fgUnitCost;
  const costMatched = Math.abs(totalRMCost - totalFGValue) < 0.01;
  const isFifoCosted = (order.materials || []).some(m => m.cost_source === 'FIFO');

  return (
    <div className="p-5 lg:p-6">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Production Orders
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{order.production_number}</h1>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full ${sc.bg} ${sc.text}`}>
                <StatusIcon className="w-3 h-3" /> {order.status}
              </span>
            </div>
            <p className="text-sm text-gray-700 font-semibold">{order.product}</p>
            <div className="flex items-center gap-4 text-[11px] text-gray-500 mt-1">
              {order.fg_item_id && <span>FG: <span className="font-mono font-semibold">{order.fg_item_id}</span></span>}
              {order.bom_number && <span>BOM: <span className="font-mono font-semibold">{order.bom_number}</span></span>}
              {order.oc_number && <span>OC: <a href={`/dashboard/sales?tab=order-confirmations&oc=${order.oc_reference || ''}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-purple-600 hover:underline cursor-pointer" data-testid="production-oc-link">{order.oc_number}</a></span>}
              {isPlanned && canUpdate ? (
                editingQty ? (
                  <span className="inline-flex items-center gap-1">
                    Qty:
                    <input type="number" min="0.01" step="any" value={editQty} autoFocus
                      data-testid="edit-fg-qty-input"
                      onChange={e => setEditQty(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveQuantity(); if (e.key === 'Escape') setEditingQty(false); }}
                      className="w-24 px-2 py-0.5 text-[11px] font-semibold border border-amber-300 rounded bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    />
                    <span className="text-[11px] text-gray-500">{order.unit}</span>
                    <button onClick={saveQuantity} disabled={savingQty} data-testid="save-fg-qty-btn"
                      className="p-0.5 text-emerald-600 hover:text-emerald-800 disabled:opacity-50">
                      {savingQty ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    </button>
                    <button onClick={() => setEditingQty(false)} data-testid="cancel-fg-qty-btn"
                      className="p-0.5 text-gray-400 hover:text-gray-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 cursor-pointer group" onClick={() => { setEditQty(String(order.quantity)); setEditingQty(true); }}
                    data-testid="fg-qty-editable" title="Click to edit FG quantity">
                    Qty: <span className="font-semibold group-hover:text-amber-600 group-hover:underline">{order.quantity} {order.unit}</span>
                    <Pencil className="w-2.5 h-2.5 text-gray-400 group-hover:text-amber-600" />
                  </span>
                )
              ) : (
                <span>Qty: <span className="font-semibold">{order.quantity} {order.unit}</span></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Download PDF - available for all statuses except Planned */}
            {order.status !== 'Planned' && !isCancelled && (
              <button onClick={generateProductionPDF} data-testid="production-download-pdf"
                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg"
                style={{ backgroundColor: '#24549c' }}>
                <Download className="w-3 h-3" /> PDF
              </button>
            )}
            {/* Cancel button - available at any status except Cancelled */}
            {canUpdate && !isCancelled && (
              <button onClick={cancelProduction} disabled={updating} data-testid="cancel-production-btn"
                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50">
                <XCircle className="w-3 h-3" /> Cancel
              </button>
            )}
            {/* Advance status button — blocked if branch not set */}
            {canUpdate && nextStatus && !isCancelled && (
              <button
                onClick={() => {
                  if (nextStatus === 'In Progress' && !order.branch) {
                    setShowBranchDialog(true);
                    return;
                  }
                  updateStatus(nextStatus);
                }}
                disabled={updating}
                data-testid="advance-status-btn"
                className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 hover:shadow-lg"
                style={{ backgroundColor: statusConfig[nextStatus]?.color || '#24549c' }}
                title={nextStatus === 'In Progress' && !order.branch ? 'Select Production Unit first' : ''}>
                {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {nextStatus === 'In Progress' ? 'Start Production' : nextStatus === 'Completed' ? 'Mark Complete' : `Move to ${nextStatus}`}
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        {!isCancelled ? (
          <div className="flex items-center gap-1 mt-4">
            {stages.map((s, i) => {
              const done = i <= currentStageIdx;
              const isCurrent = i === currentStageIdx;
              return (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-all ${done ? isCurrent ? sc.bg + ' ' + sc.text : 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                    {done && !isCurrent && <CheckCircle className="w-3 h-3" />}
                    {isCurrent && <StatusIcon className="w-3 h-3" />}
                    {s}
                  </div>
                  {i < stages.length - 1 && <div className={`flex-1 h-0.5 rounded ${done ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-xs text-red-700 font-semibold">
              <XCircle className="w-4 h-4" /> Production Cancelled
              {order.cancelled_by_name && <span className="font-normal text-red-600">by {order.cancelled_by_name}</span>}
              {order.cancelled_at && <span className="font-normal text-red-500">on {new Date(order.cancelled_at).toLocaleDateString('en-IN')}</span>}
            </div>
            {order.cancellation_reversal_log?.length > 0 && (
              <div className="mt-2 text-[11px] text-red-600 space-y-0.5">
                <p className="font-semibold">Stock Reversals:</p>
                {order.cancellation_reversal_log.map((log, i) => <p key={i}>- {log}</p>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info + Materials */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3 text-xs">
            <h3 className="font-bold text-gray-900 text-xs">Order Details</h3>
            {(order.branch_name || order.branch_code) ? (
              <div className="p-2.5 rounded-lg border" style={{ backgroundColor: '#24549c10', borderColor: '#24549c30' }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <Factory className="w-3 h-3" style={{ color: '#24549c' }} />
                    <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Production Unit</p>
                  </div>
                  {order.status === 'Planned' && (
                    <button onClick={() => setShowBranchDialog(true)} data-testid="change-branch-btn"
                      className="text-[10px] font-semibold hover:underline flex items-center gap-1" style={{ color: '#24549c' }}>
                      <Pencil className="w-2.5 h-2.5" /> Change
                    </button>
                  )}
                </div>
                <p className="font-bold text-gray-900 text-xs leading-tight" data-testid="branch-name">{order.branch_name || '-'}</p>
                {order.branch_code && <p className="text-[10px] text-gray-500 font-mono mt-0.5">Code: {order.branch_code}</p>}
                {(order.branch_city || order.branch_state) && (
                  <p className="text-[10px] text-gray-600 mt-0.5">{[order.branch_city, order.branch_state].filter(Boolean).join(', ')}</p>
                )}
              </div>
            ) : order.status === 'Planned' ? (
              <button onClick={() => setShowBranchDialog(true)} data-testid="set-branch-btn"
                className="w-full p-2.5 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 text-amber-700 font-semibold text-xs hover:bg-amber-100 transition flex items-center justify-center gap-1.5">
                <Factory className="w-3.5 h-3.5" />
                Set Production Unit (Required)
              </button>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              <div><p className="text-gray-500">Planned Start</p><p className="font-semibold">{order.planned_start || '-'}</p></div>
              <div><p className="text-gray-500">Planned End</p><p className="font-semibold">{order.planned_end || '-'}</p></div>
              {order.actual_start && <div><p className="text-gray-500">Actual Start</p><p className="font-semibold text-emerald-700">{order.actual_start}</p></div>}
              {order.actual_end && <div><p className="text-gray-500">Actual End</p><p className="font-semibold text-emerald-700">{order.actual_end}</p></div>}
            </div>
            <div><p className="text-gray-500">Created by</p><p className="font-semibold">{order.created_by_name}</p></div>
            {order.notes && <div><p className="text-gray-500">Notes</p><p className="text-gray-700">{order.notes}</p></div>}
            {order.stock_deducted && (
              <div className="p-2 bg-emerald-50 rounded-lg flex items-center gap-1.5">
                <CheckCircle className="w-3 h-3 text-emerald-600" />
                <span className="text-[11px] text-emerald-700 font-semibold">Materials deducted from stock (FIFO)</span>
              </div>
            )}
            {order.stock_added && (
              <div className="p-2 bg-blue-50 rounded-lg flex items-center gap-1.5">
                <Package className="w-3 h-3 text-blue-600" />
                <span className="text-[11px] text-blue-700 font-semibold">Finished goods added to stock ({order.quantity} {order.unit})</span>
              </div>
            )}
            {order.cost_last_edited_by && (
              <div className="p-2 bg-amber-50 rounded-lg text-[11px] text-amber-700">
                Costs last edited by <span className="font-semibold">{order.cost_last_edited_by}</span>
                {order.cost_last_edited_at && <span> on {new Date(order.cost_last_edited_at).toLocaleDateString('en-IN')}</span>}
              </div>
            )}
          </div>

          {/* Cost Summary Card */}
          {(order.materials || []).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3 text-xs" data-testid="cost-summary-card">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900 text-xs flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5" style={{ color: '#24549c' }} /> Cost Summary
                  {isFifoCosted && (
                    <span data-testid="fifo-cost-badge" title="Material rates reflect the actual FIFO cost of the stock batches consumed at production start."
                      className="px-1.5 py-0.5 text-[8px] font-bold rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                      ACTUAL FIFO
                    </span>
                  )}
                </h3>
                {isCompletedOrDispatched && canUpdate && !editingCosts && (
                  <button onClick={startEditCosts} data-testid="edit-costs-btn"
                    className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                    <Pencil className="w-2.5 h-2.5" /> Edit Costs
                  </button>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Raw Material Cost</span>
                  <span className="font-semibold font-mono text-gray-900">Rs. {totalRMCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">FG Unit Cost</span>
                  <span className="font-semibold font-mono" style={{ color: '#24549c' }}>Rs. {fgUnitCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="border-t border-gray-100 pt-2 flex justify-between">
                  <span className="text-gray-500">Total FG Produced Value</span>
                  <span className="font-semibold font-mono text-gray-900">Rs. {totalFGValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className={`p-2 rounded-lg flex items-center gap-1.5 ${costMatched ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  {costMatched
                    ? <><CheckCircle className="w-3 h-3 text-emerald-600" /><span className="text-[11px] text-emerald-700 font-semibold">RM Cost & FG Value Matched</span></>
                    : <><Package className="w-3 h-3 text-amber-600" /><span className="text-[11px] text-amber-700 font-semibold">RM: Rs.{totalRMCost.toFixed(2)} vs FG: Rs.{totalFGValue.toFixed(2)}</span></>
                  }
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xs font-bold text-gray-900">Materials ({(editingMaterials ? editMaterials : editingCosts ? editCostMaterials : order.materials)?.length || 0})</h3>
              <div className="flex items-center gap-2">
                {isPlanned && canUpdate && !editingMaterials && (
                  <button onClick={startEditMaterials} data-testid="edit-materials-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                    <Pencil className="w-3 h-3" /> Edit Materials
                  </button>
                )}
                {editingMaterials && (
                  <>
                    <button onClick={addMaterialRow} data-testid="add-material-btn"
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100">
                      <Plus className="w-3 h-3" /> Add Row
                    </button>
                    <button onClick={cancelEditMaterials} data-testid="cancel-edit-btn"
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                    <button onClick={saveMaterials} disabled={savingMaterials} data-testid="save-materials-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold rounded-lg text-white disabled:opacity-50"
                      style={{ backgroundColor: '#059669' }}>
                      {savingMaterials ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                    </button>
                  </>
                )}
                {editingCosts && (
                  <>
                    <button onClick={() => setEditingCosts(false)} data-testid="cancel-cost-edit-btn"
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                    <button onClick={saveCosts} disabled={savingCosts} data-testid="save-costs-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold rounded-lg text-white disabled:opacity-50"
                      style={{ backgroundColor: '#059669' }}>
                      {savingCosts ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Costs
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Edit mode */}
            {editingMaterials ? (
              <div className="divide-y divide-gray-50">
                {editMaterials.map((mat, i) => {
                  const isSearchingThis = productSearchIdx === i;
                  const isSearchingItemId = isSearchingThis && productSearchField === 'item_id';
                  const isSearchingDesc = isSearchingThis && productSearchField === 'description';
                  return (
                  <div key={i} className="px-4 py-4 border-b border-gray-100 hover:bg-gray-50/30">
                    {/* Row 1: seq + SKU + Product name + delete */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center font-bold text-[10px]" style={{ color: '#24549c' }}>
                        <input value={mat.seq} onChange={e => updateMaterialField(i, 'seq', e.target.value)}
                          className="w-full h-full bg-transparent text-center border-none outline-none text-[10px] font-bold" />
                      </div>
                      <div className="flex-shrink-0 w-40 relative" ref={isSearchingItemId ? productSearchRef : null}>
                        <label className="block text-[9px] text-gray-400 font-semibold uppercase mb-0.5">Item ID / SKU</label>
                        <div className="relative">
                          <input
                            value={isSearchingItemId ? productSearchText : mat.item_id}
                            onFocus={() => { setProductSearchIdx(i); setProductSearchField('item_id'); setProductSearchText(''); }}
                            onChange={e => { setProductSearchText(e.target.value); setProductSearchIdx(i); setProductSearchField('item_id'); }}
                            data-testid={`material-item-search-${i}`}
                            className="w-full px-2 py-2 border border-gray-200 rounded-md text-xs font-mono pr-6 focus:outline-none focus:ring-1 focus:ring-blue-300" placeholder="SKU..." />
                          <Search className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 translate-y-1/3" />
                        </div>
                        {isSearchingItemId && (
                          <div className="absolute z-20 top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                            {filteredProducts.length > 0 ? filteredProducts.map(p => (
                              <button key={p.id} type="button" onClick={() => selectProduct(i, p)}
                                data-testid={`product-option-${p.id}`}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-[11px] border-b border-gray-50 flex items-center gap-2">
                                <span className="font-mono text-gray-500 flex-shrink-0">{p.sku}</span>
                                <span className="font-medium text-gray-900 truncate">{p.name}</span>
                                <span className="text-gray-400 flex-shrink-0 ml-auto">{p.unit || p.buy_unit}</span>
                              </button>
                            )) : (
                              <div className="px-3 py-4 text-center text-[10px] text-gray-400">
                                {allProducts.length === 0 ? 'Loading products...' : 'No matching products found'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 relative" ref={isSearchingDesc ? productSearchRef : null}>
                        <label className="block text-[9px] text-gray-400 font-semibold uppercase mb-0.5">Product</label>
                        <div className="relative">
                          <input
                            value={isSearchingDesc ? productSearchText : mat.item_description}
                            onFocus={() => { setProductSearchIdx(i); setProductSearchField('description'); setProductSearchText(''); }}
                            onChange={e => { setProductSearchText(e.target.value); setProductSearchIdx(i); setProductSearchField('description'); }}
                            data-testid={`material-desc-search-${i}`}
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm font-semibold pr-7 focus:outline-none focus:ring-1 focus:ring-blue-300" placeholder="Type to search product..." />
                          <Search className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 top-1/2 translate-y-1/3" />
                        </div>
                        {isSearchingDesc && (
                          <div className="absolute z-20 top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                            {filteredProducts.length > 0 ? filteredProducts.map(p => (
                              <button key={p.id} type="button" onClick={() => selectProduct(i, p)}
                                data-testid={`product-option-desc-${p.id}`}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-[11px] border-b border-gray-50 flex items-center gap-2">
                                <span className="font-mono text-gray-500 flex-shrink-0">{p.sku}</span>
                                <span className="font-medium text-gray-900 truncate">{p.name}</span>
                                <span className="text-gray-400 flex-shrink-0 ml-auto">{p.unit || p.buy_unit}</span>
                              </button>
                            )) : (
                              <div className="px-3 py-4 text-center text-[10px] text-gray-400">
                                {allProducts.length === 0 ? 'Loading products...' : 'No matching products found'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <button onClick={() => removeMaterialRow(i)} data-testid={`remove-material-${i}`}
                        className="flex-shrink-0 mt-4 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Row 2: Qty | Available Stock | Unit | Rate */}
                    <div className="flex items-end gap-3 pl-10">
                      <div className="w-28">
                        <label className="block text-[9px] text-gray-400 font-semibold uppercase mb-0.5">Required Qty</label>
                        <input type="number" step="any" value={mat.quantity} onChange={e => updateMaterialField(i, 'quantity', e.target.value)}
                          data-testid={`material-qty-${i}`}
                          className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-right font-bold focus:outline-none focus:ring-1 focus:ring-blue-300" placeholder="0" />
                      </div>
                      <div className="w-40">
                        <label className="block text-[9px] text-gray-400 font-semibold uppercase mb-0.5">Available Stock</label>
                        {mat.current_stock !== undefined && mat.current_stock !== null ? (
                          <div data-testid={`material-available-${i}`} className={`px-3 py-1.5 rounded-md text-sm font-bold text-center border-2 ${mat.current_stock >= Number(mat.quantity || 0) ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-red-50 text-red-600 border-red-300'}`}>
                            <div>{Number(mat.current_stock).toLocaleString('en-IN', { maximumFractionDigits: 2 })} {mat.unit}</div>
                            {Number(mat.reserved_stock || 0) > 0 && (
                              <div className="text-[9px] font-medium text-gray-500 mt-0.5" data-testid={`material-reserved-hint-${i}`} title={`Physical ${Number(mat.physical_stock||0).toLocaleString('en-IN',{maximumFractionDigits:2})} ${mat.unit} − Reserved for OC dispatches ${Number(mat.reserved_stock).toLocaleString('en-IN',{maximumFractionDigits:2})} ${mat.unit}`}>
                                phys {Number(mat.physical_stock||0).toLocaleString('en-IN',{maximumFractionDigits:1})} − res {Number(mat.reserved_stock).toLocaleString('en-IN',{maximumFractionDigits:1})}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="px-3 py-2 rounded-md text-sm font-semibold text-center bg-gray-50 text-gray-400 border-2 border-gray-200">— Select product —</div>
                        )}
                      </div>
                      <div className="w-24">
                        <label className="block text-[9px] text-gray-400 font-semibold uppercase mb-0.5">Unit</label>
                        {(() => {
                          const allowed = mat._allowedUOMs || [];
                          if (allowed.length > 1) {
                            return (
                              <select value={mat.unit} onChange={e => changeMaterialUnit(i, e.target.value)}
                                data-testid={`material-unit-select-${i}`}
                                className="w-full px-2 py-2 border border-blue-200 rounded-md text-xs bg-blue-50 text-blue-700 font-semibold cursor-pointer"
                                title={`Available UOMs: ${allowed.join(', ')}`}>
                                {allowed.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            );
                          }
                          return (
                            <input value={mat.unit} onChange={e => updateMaterialField(i, 'unit', e.target.value)}
                              data-testid={`material-unit-input-${i}`}
                              className="w-full px-2 py-2 border border-gray-200 rounded-md text-xs" placeholder="Unit" />
                          );
                        })()}
                      </div>
                      <div className="w-28">
                        <label className="block text-[9px] text-gray-400 font-semibold uppercase mb-0.5">Rate</label>
                        <input type="number" step="any" value={mat.rate ?? ''} onChange={e => updateMaterialField(i, 'rate', e.target.value)}
                          data-testid={`material-rate-${i}`}
                          className="w-full px-2 py-2 border border-gray-200 rounded-md text-xs text-right" placeholder="0.00" />
                      </div>
                      {mat.total_cost != null && (
                        <div className="w-32">
                          <label className="block text-[9px] text-gray-400 font-semibold uppercase mb-0.5">Total Cost</label>
                          <div className="px-3 py-2 rounded-md text-xs text-right font-mono font-semibold bg-gray-50 text-gray-700 border border-gray-200">
                            ₹{Number(mat.total_cost).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
                {editMaterials.length === 0 && (
                  <div className="px-4 py-8 text-center text-xs text-gray-400">No materials. Click "Add Row" to add.</div>
                )}
              </div>
            ) : editingCosts ? (
              /* Cost Edit Mode - only rate is editable */
              <div className="divide-y divide-gray-50">
                <div className="px-4 py-2 bg-amber-50/50 text-[10px] text-amber-700 font-semibold flex items-center gap-1.5">
                  <DollarSign className="w-3 h-3" /> Editing cost prices only. Quantities are locked.
                </div>
                {editCostMaterials.map((mat, i) => (
                  <div key={i} className="flex items-center px-4 py-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-3 flex-shrink-0" style={{ backgroundColor: '#24549c15' }}>
                      <span className="text-[9px] font-bold" style={{ color: '#24549c' }}>#{mat.seq}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-gray-500">{mat.item_id}</span>
                        <span className="text-xs font-semibold text-gray-900 truncate">{mat.item_description}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] flex-shrink-0">
                      <span className="text-gray-600">{Number(mat.quantity).toFixed(2)} {mat.unit}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400">@</span>
                        <input type="number" step="any" value={mat.rate ?? ''} onChange={e => updateCostField(i, 'rate', e.target.value)}
                          data-testid={`cost-rate-input-${i}`}
                          className="w-20 px-2 py-1.5 border border-amber-300 rounded text-[11px] text-right bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-400" placeholder="Rate" />
                      </div>
                      <span className="font-mono font-semibold w-24 text-right">
                        {mat.total_cost != null ? `Rs.${Number(mat.total_cost).toLocaleString('en-IN')}` : '-'}
                      </span>
                    </div>
                  </div>
                ))}
                {/* Cost edit totals */}
                {editCostMaterials.length > 0 && (
                  <div className="px-4 py-3 bg-gray-50 flex items-center justify-end gap-6 text-[11px]">
                    <span className="text-gray-500 font-semibold">Total RM Cost:</span>
                    <span className="font-mono font-bold text-gray-900">
                      Rs. {editCostMaterials.reduce((s, m) => s + (m.total_cost || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className="text-gray-500 font-semibold">FG Unit Cost:</span>
                    <span className="font-mono font-bold" style={{ color: '#24549c' }}>
                      Rs. {(fgQty > 0 ? editCostMaterials.reduce((s, m) => s + (m.total_cost || 0), 0) / fgQty : 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              /* Read-only mode */
              <div className="divide-y divide-gray-50">
                {(order.materials || []).map((mat, i) => (
                  <div key={i} className={`flex items-center px-4 py-3 ${mat.is_alternate ? 'bg-amber-50/30' : ''}`}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center mr-3 flex-shrink-0" style={{ backgroundColor: '#24549c15' }}>
                      <span className="text-[9px] font-bold" style={{ color: '#24549c' }}>#{mat.seq}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-gray-500">{mat.item_id}</span>
                        <span className="text-xs font-semibold text-gray-900 truncate">{mat.item_description}</span>
                        {mat.is_alternate && <span className="px-1.5 py-0.5 text-[8px] font-bold bg-amber-100 text-amber-700 rounded">SWAPPED</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-[11px] flex-shrink-0">
                      {isPlanned && mat.current_stock !== undefined && mat.current_stock !== null && (
                        mat.unit_mismatch ? (
                          <span data-testid={`material-stock-${i}`}
                            title={`Stock is tracked in '${mat.stock_unit || ''}' but the BOM uses '${mat.unit}'. No conversion is defined on the product master — please add a ${mat.stock_unit || 'base'}↔${mat.unit} conversion factor on the product so we can compare correctly.`}
                            className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-300">
                            ⚠ Stock: {Number(mat.current_stock).toFixed(2)} {mat.stock_unit || mat.unit}
                            <span className="ml-1 text-[9px] font-normal text-amber-600">(unit mismatch — add {mat.stock_unit}→{mat.unit} conversion)</span>
                          </span>
                        ) : (
                          <span data-testid={`material-stock-${i}`}
                            title={Number(mat.reserved_stock || 0) > 0
                              ? `Physical: ${Number(mat.physical_stock||0).toFixed(2)} ${mat.unit} − Reserved for Awaiting-Invoice OC dispatches: ${Number(mat.reserved_stock).toFixed(2)} ${mat.unit} = Available for production: ${Number(mat.current_stock).toFixed(2)} ${mat.unit}`
                              : `Available for production`}
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold ${mat.current_stock >= Number(mat.quantity) ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                            Stock: {Number(mat.current_stock).toFixed(2)} {mat.unit}
                            {Number(mat.reserved_stock || 0) > 0 && (
                              <span className="ml-1 text-[9px] font-normal text-gray-500" data-testid={`material-reserved-${i}`}>
                                (phys {Number(mat.physical_stock || 0).toFixed(1)} − {Number(mat.reserved_stock).toFixed(1)} reserved)
                              </span>
                            )}
                          </span>
                        )
                      )}
                      <span className="text-gray-600">{Number(mat.quantity).toFixed(2)} {mat.unit}</span>
                      <span className="font-mono text-gray-500">{mat.rate != null ? `@${mat.rate}` : ''}</span>
                      <span className="font-mono font-semibold">{mat.total_cost != null ? `Rs.${Number(mat.total_cost).toLocaleString('en-IN')}` : ''}</span>
                    </div>
                  </div>
                ))}
                {(!order.materials || order.materials.length === 0) && (
                  <div className="px-4 py-8 text-center text-xs text-gray-400">No materials linked</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Branch Selection Dialog */}
      {showBranchDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !savingBranch && setShowBranchDialog(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()} data-testid="branch-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Factory className="w-4 h-4" style={{ color: '#24549c' }} />
                <h3 className="text-sm font-bold text-gray-900">{order.branch ? 'Change Production Unit' : 'Select Production Unit'}</h3>
              </div>
              <button onClick={() => !savingBranch && setShowBranchDialog(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Raw material stock will be deducted from the selected branch. Finished goods will be added to it.</p>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Production Branch *</label>
            <select value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)} data-testid="branch-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-200">
              <option value="">-- Select production unit --</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
              ))}
            </select>
            {branches.length === 0 && (
              <p className="text-[10px] text-amber-600 mt-1">No Production-type branches found. Create one in Settings → Branches.</p>
            )}
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setShowBranchDialog(false)} disabled={savingBranch} data-testid="branch-cancel-btn"
                className="px-3 py-2 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={saveBranch} disabled={!selectedBranchId || savingBranch} data-testid="branch-save-btn"
                className="px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 flex items-center gap-1.5" style={{ backgroundColor: '#24549c' }}>
                {savingBranch ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                {savingBranch ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionDetailView;
